/**
 * SalesLandingChat - the /so landing experience.
 *
 * Hero state (every visit): only the brand icon, a title, and the real
 * ChatComposer in the same bordered card the docked pane uses - no message
 * list (ChatGPT new-chat style). On the first submit it (1) creates a FRESH
 * chat session (the landing never shows history), (2) sends the run into it,
 * and (3) docks: the full AiChatPane mounts with a slide/fade animation so
 * the input lands at the bottom as the real composer.
 *
 * Session continuity: the WS session lives in ChatSessionProvider above the
 * router, so when the copilot navigates to a deal page the same conversation
 * continues in the right-rail chat.
 */

import type { AiMessageActionHandler, ChatMode } from '@protoss/shared';
import * as React from 'react';
import { AiBrandIcon } from '../../../components/shared/AiBrandIcon';
import { AiChatPane } from '../../ai-chat/components/AiChatPane';
import { ChatComposer } from '../../ai-chat/components/ChatComposer';
import type { SendRunOptions, UseChatSessionReturn } from '../../ai-chat/hooks/useChatSession';

export function SalesLandingChat({
  chatSession,
  aiAssistantName,
  onAction,
  workspaceUuid,
}: {
  chatSession: UseChatSessionReturn;
  aiAssistantName: string;
  onAction?: AiMessageActionHandler;
  workspaceUuid: string;
}) {
  const [started, setStarted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // Hero mode is LOCAL and always starts at 'ask'. The shared composerMode
  // tracks the rail session's last-used mode (chat_session.mode), which may be
  // 'agent' - a fresh new-chat surface must not inherit it. On submit the
  // chosen mode seeds the fresh session, so the docked pane stays consistent.
  const [heroMode, setHeroMode] = React.useState<ChatMode>('ask');

  const handleHeroSend = async ({ prompt, model, mode, commandKey }: SendRunOptions) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Reuse the active session if it has no messages yet (auto-created as a blank
      // placeholder by the empty-sessions effect). Only create a fresh session when
      // the active one has history or an in-flight run, to avoid leaving an orphaned
      // "Untitled" session behind.
      const activeUuid = chatSession.state.activeSessionUuid;
      const activeSession = chatSession.state.sessions.find((s) => s.uuid === activeUuid);
      const isEmpty =
        activeUuid && activeSession && !activeSession.lastMessageAt && !chatSession.isRunActive;

      const uuid = isEmpty ? activeUuid : await chatSession.createNewSession({ mode });
      chatSession.sendRun({
        prompt,
        model,
        mode,
        commandKey,
        // Explicit target - sendRun must not read the stale activeSessionUuid
        // (state syncs to refs on the next render, after this await).
        sessionUuid: uuid ?? undefined,
      });
      setStarted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!started) {
    return (
      <div className="sales-landing-hero">
        {/* Only the composer participates in the vertical centering - the
            header hangs above it out-of-flow, so the INPUT sits at mid-pane. */}
        <div className="sales-landing-hero__center">
          <div className="sales-landing-hero__header">
            <AiBrandIcon className="deal-page__ai-icon deal-page__ai-icon--md" />
            <h2 className="sales-landing-hero__title">How can {aiAssistantName} help you today?</h2>
          </div>
          <div className="ai-chat-bottom-card sales-landing-hero__composer">
            <ChatComposer
              explicitContextItems={[]}
              onRemoveExplicitContext={() => {}}
              onClearExplicitContext={() => {}}
              isRunActive={false}
              isConnected={chatSession.isConnected}
              autoFocus
              mode={heroMode}
              onModeChange={setHeroMode}
              onSend={(opts) => void handleHeroSend(opts)}
              onStop={() => {}}
              connectionStatus={chatSession.state.connectionStatus}
              reconnectAttempt={chatSession.state.reconnectAttempt}
              onReconnect={chatSession.manualReconnect}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-landing-chat sales-landing-chat--started">
      <div className="sales-landing-chat__container">
        <div className="sales-landing-chat__pane">
          <AiChatPane
            state={chatSession.state}
            isConnected={chatSession.isConnected}
            isRunActive={chatSession.isRunActive}
            sendRun={chatSession.sendRun}
            cancelRun={chatSession.cancelRun}
            confirmRun={chatSession.confirmRun}
            denyRun={chatSession.denyRun}
            respondPrompt={chatSession.respondPrompt}
            retryRun={chatSession.retryRun}
            updateContext={chatSession.updateContext}
            setDraftExplicitContext={chatSession.setDraftExplicitContext}
            confirmDraftExplicitContext={chatSession.confirmDraftExplicitContext}
            clearDraftExplicitContext={chatSession.clearDraftExplicitContext}
            removeExplicitContext={chatSession.removeExplicitContext}
            clearExplicitContext={chatSession.clearExplicitContext}
            loadOlderHistory={chatSession.loadOlderHistory}
            setComposerMode={chatSession.setComposerMode}
            projectUuid={workspaceUuid}
            onAction={onAction}
            onActionSettled={chatSession.setMessageActionResult}
            autoFocusComposer
            connectionStatus={chatSession.state.connectionStatus}
            reconnectAttempt={chatSession.state.reconnectAttempt}
            onReconnect={chatSession.manualReconnect}
          />
        </div>
      </div>
    </div>
  );
}
